import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Check, ChevronDown, Download, FileDown, HelpCircle, Info, Save } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { downloadDiagramPNG, downloadDiagramPDF } from '../utils/diagramExport';
import { useSEO } from '../components/SEO';
import { Reveal, Overline, EASE } from '../components/motion';
import { GarageDiagram } from '../components/GarageDiagram';
import { PanelSketch } from '../components/PanelSketch';
import { RAL, WOOD_SPECIAL } from '../components/GarageFinishes';
import { LeadForm } from '../components/LeadForm';

// jsPDF built-in fonts lack Romanian diacritics — strip them for PDF text
const strip = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '');

const DIM_KEYS = ['latime_gol', 'inaltime_gol', 'spatiu_stanga', 'spatiu_dreapta', 'lintel', 'adancime'];

const PANEL_MODELS = [
  { key: 'single', name: 'SINGLE' },
  { key: 'multi', name: 'MULTI' },
  { key: 'flat', name: 'FLAT' },
  { key: 'blocks', name: 'BLOCKS' },
  { key: 'multirib', name: 'MULTI RIB' },
];

// text-base (16px) minimum: prevents iOS Safari auto-zoom on input focus
const inputCls =
  'w-full bg-transparent border-b border-line/20 py-3 text-ink placeholder-smoke/60 text-base focus:border-accent focus:outline-none transition-colors duration-300';
const labelCls = 'text-[10px] font-bold uppercase tracking-[0.2em] text-smoke';

export default function Configurator() {
  const { t } = useLanguage();
  const c = t('configurator');
  useSEO('configurator');
  const [dims, setDims] = useState({});
  const [active, setActive] = useState(null);
  const [step, setStep] = useState('measure');
  const [dimError, setDimError] = useState('');
  const [color, setColor] = useState('');
  const [model, setModel] = useState('');
  const [colorOpen, setColorOpen] = useState(false);

  const setDim = (k, v) => { setDims((d) => ({ ...d, [k]: v })); setDimError(''); };
  const cleanDims = Object.fromEntries(Object.entries(dims).filter(([, v]) => v && v.toString().trim() !== ''));
  const leadMeasurements = {
    ...cleanDims,
    ...(model ? { model } : {}),
    ...(color ? { culoare: color } : {}),
  };

  const gf = t('garageFinishes');
  const finishOptions = [
    ...RAL.map((r, i) => ({
      key: `ral-${i}`,
      name: (gf.ralNames && gf.ralNames[i]) || r.name,
      value: `${(gf.ralNames && gf.ralNames[i]) || r.name} (${r.ral})`,
      bg: r.bg,
      hex: r.hex,
      stops: r.stops || null,
      border: r.border,
    })),
    ...WOOD_SPECIAL.map((w, i) => ({
      key: `wood-${i}`,
      name: (gf.woodNames && gf.woodNames[i]) || w.name,
      value: (gf.woodNames && gf.woodNames[i]) || w.name,
      bg: w.bg,
      hex: w.hex,
      stops: w.stops || null,
    })),
  ];
  const selectedOption = finishOptions.find((o) => o.value === color);

  const downloadPNG = () => downloadDiagramPNG('configuratie-porti-garaj.png');

  const downloadPDF = async () => {
    const rows = DIM_KEYS.filter((k) => cleanDims[k]).map((k) => {
      const m = c.dims[k].label.match(/\(([^)]+)\)/);
      return `${m ? m[1] : c.dims[k].label}: ${cleanDims[k]} ${c.unit}`;
    });
    if (model) rows.push(`${strip(c.modelLabel)}: ${strip(model)}`);
    if (color) rows.push(`${strip(c.colorLabel)}: ${strip(color)}`);
    await downloadDiagramPDF({
      title: 'Domidoor Solutions - Configuratie porti de garaj',
      rows,
      filename: 'configuratie-porti-garaj.pdf',
    });
  };

  const save = () => {
    if (!dims.latime_gol || !dims.inaltime_gol) { setDimError(c.errDims); return; }
    setStep('prompt');
  };

  return (
    <main data-testid="configurator-page" className="pt-32 md:pt-36 lg:pt-44 pb-24 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal className="mb-12 max-w-3xl">
          <Overline className="mb-4">{c.overline}</Overline>
          <h1 className="font-display text-3xl md:text-5xl font-medium tracking-tight mb-4">{c.title}</h1>
          <p className="text-smoke leading-relaxed">{c.intro}</p>
        </Reveal>

        <AnimatePresence mode="wait">
          {step === 'measure' && (
            <motion.div key="measure" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: EASE }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <p data-testid="configurator-dims-title" className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-6">{c.dimsTitle}</p>
                <div className="space-y-5">
                  {DIM_KEYS.map((k) => (
                    <div key={k} data-testid={`dim-field-${k}`} onMouseEnter={() => setActive(k)} onMouseLeave={() => setActive(null)}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <label className={labelCls}>{c.dims[k].label}</label>
                        <span className="dd-tip text-smoke hover:text-accent transition-colors cursor-help" data-testid={`dim-help-${k}`} tabIndex={0}>
                          <HelpCircle size={14} />
                          <span className="dd-tip-body">{c.dims[k].help}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="0" inputMode="numeric"
                          data-testid={`dim-input-${k}`}
                          className={inputCls}
                          value={dims[k] || ''}
                          onChange={(e) => setDim(k, e.target.value)}
                          onFocus={() => setActive(k)}
                          onBlur={() => setActive(null)}
                          placeholder="0"
                        />
                        <span className="text-smoke text-xs shrink-0">{c.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-10">
                  <p data-testid="configurator-finish-title" className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-5">{c.finishTitle}</p>
                  <p className={`${labelCls} block mb-3`}>{c.modelLabel}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                    {PANEL_MODELS.map((m) => {
                      const selected = model === m.name;
                      return (
                        <div key={m.key} className="relative">
                          <button type="button" data-testid={`model-option-${m.key}`} onClick={() => setModel(selected ? '' : m.name)} className="text-left group w-full">
                            <span
                              className={`block h-24 md:h-28 rounded-2xl bg-surface p-2 transition-all duration-300 ${selected ? 'ring-2 ring-accent ring-offset-2 ring-offset-paper' : 'group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)]'} border border-line/15`}
                            >
                              <PanelSketch model={m.key} />
                            </span>
                            <span className={`block mt-2 text-sm font-display font-medium ${selected ? 'text-accent font-bold' : 'text-ink'}`}>{m.name}</span>
                          </button>
                          <div className="absolute top-2.5 right-2.5 z-20">
                            <span
                              className="dd-tip dd-tip-right w-6 h-6 grid place-items-center rounded-full border border-line/20 bg-paper text-smoke hover:text-accent hover:border-accent transition-colors duration-300 cursor-help"
                              data-testid={`model-info-${m.key}`}
                              tabIndex={0}
                            >
                              <Info size={13} />
                              <span className="dd-tip-body">{c.modelDescs[m.key]}</span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className={`${labelCls} block mb-3`}>{c.colorLabel}</p>
                  {/* mobile: dropdown list with swatches */}
                  <div className="md:hidden">
                    <button
                      type="button"
                      data-testid="color-dropdown-toggle"
                      onClick={() => setColorOpen((v) => !v)}
                      className="w-full flex items-center gap-3 border border-line/20 rounded-2xl px-4 py-3.5 bg-surface"
                    >
                      {selectedOption ? (
                        <span className={`h-9 w-14 rounded-lg shrink-0 ${selectedOption.border ? 'border border-line/15' : ''}`} style={{ background: selectedOption.bg }} />
                      ) : (
                        <span className="h-9 w-14 rounded-lg shrink-0 border border-dashed border-line/30" />
                      )}
                      <span className={`flex-1 text-left text-sm ${selectedOption ? 'text-ink font-medium' : 'text-smoke'}`}>
                        {selectedOption ? selectedOption.name : c.colorPlaceholder}
                      </span>
                      <ChevronDown size={16} className={`text-smoke transition-transform duration-300 ${colorOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {colorOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, ease: EASE }}
                          data-testid="color-dropdown-list"
                          className="mt-2 border border-line/15 rounded-2xl bg-surface divide-y divide-line/10 max-h-80 overflow-y-auto shadow-[0_24px_70px_rgba(24,26,32,0.10)]"
                        >
                          {finishOptions.map((o) => (
                            <button
                              key={o.key}
                              type="button"
                              data-testid={`color-option-mobile-${o.key}`}
                              onClick={() => { setColor(color === o.value ? '' : o.value); setColorOpen(false); }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left"
                            >
                              <span className={`h-9 w-14 rounded-lg shrink-0 ${o.border ? 'border border-line/15' : ''}`} style={{ background: o.bg }} />
                              <span className={`flex-1 text-sm ${color === o.value ? 'text-accent font-bold' : 'text-ink'}`}>{o.name}</span>
                              {color === o.value && <Check size={16} className="text-accent shrink-0" />}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {/* desktop: larger swatch grid */}
                  <div className="hidden md:grid grid-cols-4 lg:grid-cols-5 gap-4">
                    {finishOptions.map((o) => {
                      const selected = color === o.value;
                      return (
                        <button key={o.key} type="button" data-testid={`color-option-${o.key}`} onClick={() => setColor(selected ? '' : o.value)} className="text-left group">
                          <span
                            className={`block h-20 rounded-2xl transition-all duration-300 ${selected ? 'ring-2 ring-accent ring-offset-2 ring-offset-paper' : 'group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(24,26,32,0.15)]'} ${o.border ? 'border border-line/15' : ''}`}
                            style={{ background: o.bg }}
                          />
                          <span className={`block mt-2 text-xs leading-tight ${selected ? 'text-accent font-bold' : 'text-smoke'}`}>{o.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {dimError && <p data-testid="configurator-dim-error" className="text-[#EF4444] text-xs mt-4">{dimError}</p>}
                <button
                  data-testid="configurator-save-button"
                  onClick={save}
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 rounded-full hover:bg-accenth hover:shadow-[0_12px_35px_rgba(200,16,46,0.3)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Save size={16} /> {c.saveBtn}
                </button>
              </div>
              <div className="lg:col-span-7">
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent mb-6">{c.diagramTitle}</p>
                <div data-testid="configurator-diagram" className="bg-surface border border-line/10 rounded-[2rem] p-6 md:p-10 shadow-[0_24px_70px_rgba(24,26,32,0.08)]">
                  <GarageDiagram active={active} onHover={setActive} caption={c.viewCaption} values={dims} />
                </div>
              </div>
            </motion.div>
          )}

          {step === 'prompt' && (
            <motion.div key="prompt" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: EASE }} className="max-w-2xl mx-auto text-center bg-surface border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-12 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <CheckCircle2 size={44} className="text-accent mx-auto mb-4" />
              <h2 data-testid="configurator-saved-title" className="font-display text-2xl md:text-3xl font-medium mb-3">{c.savedTitle}</h2>
              <div data-testid="configurator-dims-summary" className="flex flex-wrap justify-center gap-2 mb-6">
                {DIM_KEYS.filter((k) => cleanDims[k]).map((k) => (
                  <span key={k} className="text-xs bg-paper border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.dims[k].label}: <b className="text-ink">{cleanDims[k]} {c.unit}</b></span>
                ))}
                {model && <span className="text-xs bg-paper border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.modelLabel}: <b className="text-ink">{model}</b></span>}
                {color && <span className="text-xs bg-paper border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.colorLabel}: <b className="text-ink">{color}</b></span>}
              </div>
              <div data-testid="configurator-prompt-diagram" className="bg-paper border border-line/10 rounded-2xl p-4 mb-6">
                <GarageDiagram active={null} onHover={null} caption={c.viewCaption} values={dims} />
              </div>
              <p className="text-smoke mb-8">{c.savedMsg}</p>
              <div className="flex flex-col items-center gap-5">
                <div className="flex flex-wrap justify-center gap-3">
                  <button data-testid="configurator-quote-yes" onClick={() => setStep('form')} className="bg-accent text-white font-bold text-xs uppercase tracking-[0.12em] px-7 py-3.5 rounded-full hover:bg-accenth transition-colors duration-300">{c.yesQuote}</button>
                  <button data-testid="configurator-download-button" onClick={downloadPNG} className="inline-flex items-center justify-center gap-2 border border-accent text-accent font-bold text-xs uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-accent hover:text-white transition-colors duration-300"><Download size={14} /> {c.downloadBtn}</button>
                  <button data-testid="configurator-download-pdf-button" onClick={downloadPDF} className="inline-flex items-center justify-center gap-2 border border-accent text-accent font-bold text-xs uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-accent hover:text-white transition-colors duration-300"><FileDown size={14} /> {c.downloadPdfBtn}</button>
                </div>
                <button data-testid="configurator-quote-no" onClick={() => setStep('done')} className="text-smoke font-bold text-[11px] uppercase tracking-[0.18em] underline-offset-4 hover:text-accent hover:underline transition-colors duration-300">{c.noThanks}</button>
              </div>
            </motion.div>
          )}

          {step === 'form' && (
            <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.4, ease: EASE }} className="max-w-2xl mx-auto">
              <h2 data-testid="configurator-form-title" className="font-display text-2xl md:text-3xl font-medium mb-2">{c.formTitle}</h2>
              <p className="text-smoke text-sm mb-6">{c.formSub}</p>
              <div data-testid="configurator-dims-summary-form" className="flex flex-wrap gap-2 mb-8">
                {DIM_KEYS.filter((k) => cleanDims[k]).map((k) => (
                  <span key={k} className="text-xs bg-surface border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.dims[k].label}: <b className="text-ink">{cleanDims[k]} {c.unit}</b></span>
                ))}
                {model && <span className="text-xs bg-surface border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.modelLabel}: <b className="text-ink">{model}</b></span>}
                {color && <span className="text-xs bg-surface border border-line/15 rounded-full px-3 py-1.5 text-smoke">{c.colorLabel}: <b className="text-ink">{color}</b></span>}
              </div>
              <div className="bg-paper border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-10 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
                <LeadForm testIdPrefix="configurator-lead-form" defaultServices={['garaj']} measurements={leadMeasurements} />
              </div>
            </motion.div>
          )}

          {step === 'done' && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: EASE }} className="max-w-2xl mx-auto text-center bg-surface border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-12 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <CheckCircle2 size={44} className="text-accent mx-auto mb-4" />
              <h2 data-testid="configurator-done-title" className="font-display text-2xl md:text-3xl font-medium mb-3">{c.doneTitle}</h2>
              <p className="text-smoke mb-6">{c.doneMsg}</p>
              <div data-testid="configurator-done-diagram" className="bg-paper border border-line/10 rounded-2xl p-4 mb-6">
                <GarageDiagram active={null} onHover={null} caption={c.viewCaption} values={dims} />
              </div>
              <div className="flex flex-wrap gap-3 justify-center">
                <button data-testid="configurator-download-done-button" onClick={downloadPNG} className="inline-flex items-center justify-center gap-2 bg-accent text-white font-bold text-xs uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-accenth transition-colors duration-300"><Download size={14} /> {c.downloadBtn}</button>
                <button data-testid="configurator-download-pdf-done-button" onClick={downloadPDF} className="inline-flex items-center justify-center gap-2 border border-accent text-accent font-bold text-xs uppercase tracking-[0.12em] px-6 py-3.5 rounded-full hover:bg-accent hover:text-white transition-colors duration-300"><FileDown size={14} /> {c.downloadPdfBtn}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
